import { ReactElement, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { CardContent, Typography, Stack } from '@mui/material';
import { LocationOn, South, HourglassEmpty, RouteOutlined, Lock } from '@mui/icons-material';

import { time, timeDurationShort } from '@/utilities/timeUtils';
import { formatDistanceKm } from '@/utilities/distanceUtils';
import { numberZeroOrBlank } from '@/utilities/preconditions';
import { DetailedRoute } from '@/generated/model';
import { ctxRoutes } from '@/contexts/RoutesCtx';
import FlagsWithGeoAdress from '@/components/common/FlagsWithGeoAddr';

export function RouteInfoCard({
  route,
  header,
  footer,
  privateRoutes,
}: {
  route: DetailedRoute;
  header?: ReactElement;
  footer?: ReactElement;
  privateRoutes?: { from: boolean; to: boolean };
}) {
  const { t } = useTranslation();
  const ctxRoute = useContext(ctxRoutes);
  const isSummary = privateRoutes !== undefined;
  return (
    <CardContent sx={{ p: '0.5rem !important', fontSize: '14px' }}>
      {header}
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        <Typography
          variant="subtitle2"
          className={`route-time-tag ${privateRoutes ? 'route-time-tag-summary' : ''}`}
          data-qa-element-id={`route_${isSummary ? 'summary_' : ''}start_time`}
        >
          {time(route.startPosition.timeMicros)}
        </Typography>
        {route.privateRoute ? (
          <Lock
            className="route-location-icon"
            data-qa-element-id={`route_${isSummary ? 'summary_' : ''}start_type_icon`}
          />
        ) : (
          <LocationOn
            className="route-location-icon"
            data-qa-element-id={`route_${isSummary ? 'summary_' : ''}start_type_icon`}
          />
        )}
        <div data-qa-element-id={`route_${isSummary ? 'summary_' : ''}start_address`}>
          {route.privateRoute && !route.startPosition.geocodedAddress && privateRoutes?.from ? (
            <Typography variant="body2">{t('routes.privateRoute')}</Typography>
          ) : (
            <FlagsWithGeoAdress
              countryCode={route.startPosition.countryCode}
              countryName={route.startPosition.countryName}
              geocodedAddress={
                ctxRoute?.poiNamesEnabled && route.startPosition.poi
                  ? route.startPosition.poi
                  : route.startPosition.geocodedAddress
              }
            />
          )}
        </div>
      </Stack>

      <div className="route-distance-details">
        <South
          className="route-details-type-icon"
          data-qa-element-id={`${isSummary ? 'summary_' : ''}arrow_down_icon`}
        />
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
          <Typography variant="subtitle2" data-qa-element-id={`route_${isSummary ? 'summary_' : ''}duration`}>
            {timeDurationShort(t, route.durationTimeMicros)}
          </Typography>
          <RouteOutlined
            fontSize="small"
            color="secondary"
            data-qa-element-id={`route_${isSummary ? 'summary_' : ''}duration_length_separator_icon`}
          />
          <Typography variant="subtitle2" data-qa-element-id={`route_${isSummary ? 'summary_' : ''}length`}>
            {formatDistanceKm(route.distanceKm)}
          </Typography>
        </Stack>
      </div>

      {route.stopPosition && (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Typography
            variant="subtitle2"
            className="route-time-tag route-time-tag-destination"
            data-qa-element-id={`route_${isSummary ? 'summary_' : ''}end_time`}
          >
            {time(route.stopPosition.timeMicros)}
          </Typography>
          {route.privateRoute ? (
            <Lock
              className="route-location-icon"
              data-qa-element-id={`route_${isSummary ? 'summary_' : ''}end_type_icon`}
            />
          ) : (
            <LocationOn
              className="route-location-icon"
              data-qa-element-id={`route_${isSummary ? 'summary_' : ''}end_type_icon`}
            />
          )}
          <div data-qa-element-id={`route_${isSummary ? 'summary_' : ''}end_address`}>
            {route.privateRoute && !route.stopPosition.geocodedAddress && privateRoutes?.to ? (
              <Typography variant="body2">{t('routes.privateRoute')}</Typography>
            ) : (
              <FlagsWithGeoAdress
                countryCode={route.stopPosition.countryCode}
                countryName={route.stopPosition.countryName}
                geocodedAddress={
                  ctxRoute?.poiNamesEnabled && route.stopPosition.poi
                    ? route.stopPosition.poi
                    : route.stopPosition.geocodedAddress
                }
              />
            )}
          </div>
        </Stack>
      )}

      <div className="route-distance-details">
        <HourglassEmpty
          className="route-details-type-icon"
          data-qa-element-id={`route_${isSummary ? 'summary_' : ''}hourglass_icon`}
        />
        <Typography
          variant="subtitle2"
          data-qa-element-id={`route_${isSummary ? 'summary_' : ''}stop_time_between_routes`}
        >
          {numberZeroOrBlank(route.standTimeMicros)
            ? t('general.data.outOfInterval')
            : timeDurationShort(t, route.standTimeMicros)}
        </Typography>
      </div>
      {footer}
    </CardContent>
  );
}
